// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import React, { FC, useState } from "react";
import { css } from "styled-components";

// Starter Custom Code
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { productListItems } from "@store/Themes/common/props/productListItems";
import textToSafeId from "@toolkit/Framework/Common/Utilities/textToSafeId";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // Starter Custom Code
    wrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    wrapper: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            ${contentSpacingStyles("0.5em", 0, 0)}
                        `,
                        null,
                        css`
                            ${contentSpacingStyles(0, 0, pxToRem(20))}
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    accordionSection: {
        css: css``,
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: ${pxToRem(350)};
                padding: 0 ${pxToRem(20)} ${pxToRem(26)};
                background-color: ${getColor("common.background")}; /* Store Custom Code */
                border: none;
                border-radius: 0 0 ${pxToRem(16)} ${pxToRem(16)}; /* Store Custom Code */

                ::-webkit-scrollbar {
                    width: 0.5em;
                }

                /* Track */
                ::-webkit-scrollbar-track {
                    background: ${getColor("common.border")};
                }

                /* Handle */
                ::-webkit-scrollbar-thumb {
                    background: ${getColor("primary")};
                }
            `,
        },
        headerProps: {
            css: css`
                background: none; /* Store Custom Code */
                button {
                    background-color: ${getColor("common.background")}; /* Store Custom Code */
                    border: ${pxToRem(2)} solid transparent;
                    padding: ${pxToRem(15)};
                    border-radius: ${pxToRem(16)}; /* Store Custom Code */

                    &:hover * {
                        color: ${getColor("secondary")};
                    }

                    &:focus {
                        padding: ${pxToRem(15)};
                        border-style: solid;
                    }
                    ,
                    :hover,
                    :active {
                        padding: ${pxToRem(15)};
                    }

                    /* Store Custom Code - Start */
                    &[aria-expanded="true"] {
                        border-radius: ${pxToRem(16)} ${pxToRem(16)} 0 0;
                    }

                    /* Store Custom Code - End */
                }
            `,
        },
        titleTypographyProps: {
            forwardAs: "h2",
            variant: "h5",
            css: css`
                padding-left: ${pxToRem(5)};
                margin: auto 0;

                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.base};
                    font-weight: ${theme.design.typography.weight.base};
                `}
            `,
        },
        /* Store Custom Code - Start */
        toggleIconProps: {
            css: css`
                color: ${getColor("primary")};
            `,
        },
        /* Store Custom Code - End */
    },
    checkBoxGroup: {
        css: css`
            width: 100%;

            &:first-of-type {
                padding-top: ${pxToRem(15)};
                border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            display: flex;
            align-items: center; /* Store Custom Code */
            margin-bottom: ${pxToRem(10)}; /* Store Custom Code */
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            display: flex;
            align-items: center;
            margin-bottom: ${pxToRem(10)}; /* Store Custom Code */
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
                font-weight: ${({ theme }) => theme.design.typography.weight.bold}; /* Store Custom Code */
            `,
        },
    },
    seeAllLink: productListItems.link,
    seeLessLink: productListItems.link,
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const count = 0;

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);
    const [accordionId] = useState(textToSafeId(title)); // Starter Custom Code

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    return (
        <StyledWrapper
            {...styles.wrapper}
            /* Starter Custom Code */ data-test-selector={`productList_filterAccordion_section_${accordionId}`} /* Store Custom Code */
        >
            <ManagedAccordionSection
                title={title}
                uid={accordionId /* Starter Custom Code */}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
                data-test-selector={`productList_filterAccordion_${accordionId}`} /* Store Custom Code */
            >
                {limitedFacets?.map(facet => (
                    <CheckboxGroup
                        key={facet.id}
                        {...styles.checkBoxGroup}
                        data-test-selector={`productList_filterAccordion_facetGroup_${facet.id}`} /* Store Custom Code */
                    >
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`productList_filterAccordion_facetFilter_${facet.id}`} /* Store Custom Code */
                            uid={facet.id /* Starter Custom Code */}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                            {/* Starter Custom Code - > */}
                            {facet.count !== -1 && (
                                <VisuallyHidden>{translate(`item${facet.count > 1 ? "s" : null}`)}</VisuallyHidden>
                            )}
                            {/* <--- Starter Custom Code */}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link
                            {...styles.seeLessLink}
                            onClick={() => setExpanded(false)}
                            data-test-selector={`productList_filterAccordion_seeLess_${accordionId}`} /* Store Custom Code */
                        >
                            {translate("See Less")}
                        </Link>
                    ) : (
                        <Link
                            {...styles.seeAllLink}
                            onClick={() => setExpanded(true)}
                            data-test-selector={`productList_filterAccordion_seeAll_${accordionId}`} /* Store Custom Code */
                        >
                            {translate("See All")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default ProductListFiltersAccordionSection;
